import React from 'react';

import { useIntl } from 'react-intl-next';

import AtlaskitForm from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { type ValueType } from '@atlaskit/select';
import {
	CancelButton,
	Card,
	CloseButton,
	DateTime,
	EditDetailsButton,
	Footer,
	OverrideButton,
	ReadonlyUser,
	Row,
	commonMessages as scheduleCommonMessages,
	useCallPromise,
} from '@atlassian/eoc-schedule-common';

import { useCurrentUser } from '../../../../utils/hooks/current-user';
import { messages as commonMessages } from '../../../messages';
import { type AutoCompleteWithNoOneItem } from '../../auto-complete-with-noone/custom-option/types';
import { OverridenWith } from '../overriden-with';

import { type FormFields, type SingleQuickOverrideShiftCardProps } from './types';

const formStyles = xcss({
	margin: '0',
	marginTop: 'space.negative.200',
});

const SingleQuickOverrideShiftCard = ({
	cards,
	onClose,
	onCancel,
	onCustomize,
	onAddOverride,
	isDisabled,
}: SingleQuickOverrideShiftCardProps) => {
	const intlApi = useIntl();

	const { isLoading: isAddOverrideLoading, call: addOverride } = useCallPromise(onAddOverride);

	const current = useCurrentUser();

	const singleCard = cards[0];

	const { rotation, ...period } = singleCard;
	const { responder } = period;

	return (
		<Card
			role="dialog"
			aria-labelledby="shift-card-title"
			controls={<CloseButton onClick={onClose} />}
			content={
				<Stack space="space.200">
					<Heading id="shift-card-title" as="h2" size="xsmall">
						{intlApi.formatMessage(commonMessages.quickOverrideTitle)}
					</Heading>

					<Stack space="space.050">
						<DateTime
							variant="start"
							epoch={period.startEpoch + period.startOffset}
							isTrueTime={period.isTrueStartTime}
						/>

						<DateTime
							variant="end"
							epoch={period.endEpoch + period.endOffset}
							isTrueTime={period.isTrueEndTime}
						/>

						<Row variant="inline" label={intlApi.formatMessage(scheduleCommonMessages.rotation)}>
							{rotation.name}
						</Row>

						<ReadonlyUser variant="shiftParticipant" responder={responder} />
					</Stack>

					<AtlaskitForm<FormFields> onSubmit={({ responder }) => addOverride(responder)}>
						{({ formProps, getState }) => (
							<Box as="form" {...formProps} xcss={formStyles}>
								<Stack space="space.200" alignInline="start">
									<OverridenWith
										isLoading={current.isLoading}
										isDisabled={isDisabled}
										defaultValue={current.user as ValueType<AutoCompleteWithNoOneItem, false>}
									/>

									<EditDetailsButton
										id="customize-override-button"
										onClick={() => {
											const {
												values: { responder },
												dirty,
											} = getState();

											// NOTE: if is not modified, the responder is JSM user. (those must be ignored)
											// else it is guaranteed that responder is RequestedResponder (with OpsGenie ID)
											onCustomize(dirty && responder ? responder : undefined);
										}}
									/>

									<Footer>
										<CancelButton onClick={onCancel} />
										<OverrideButton isLoading={isAddOverrideLoading} type="submit" />
									</Footer>
								</Stack>
							</Box>
						)}
					</AtlaskitForm>
				</Stack>
			}
		/>
	);
};

export { SingleQuickOverrideShiftCard };
